import type { AgentTaskType } from '@septima/contract';
// Agent metadata registry for the Agents Kanban — the one source of truth for
// each agent's display code, owning surface, and accent colour. Shared by the
// board (task cards) and the Needs-Review item cards so a given agent's colour
// matches across every column.

export interface AgentMeta {
    code: string;
    fullName: string;
    taskTypes: AgentTaskType[];
    surface: 'desk' | 'ops' | 'comply' | 'view';
    /** Accent colour as an "r, g, b" triplet for `rgb(var(--agent))`. */
    color: string;
}

export const AGENTS: AgentMeta[] = [
    {
        code: 'ATLAS',
        fullName: 'Post-call Processing & Reports',
        taskTypes: [
            'post_call_processing',
            'call_classification_batch',
            'post_call_processing_batch',
            'fact_find_prefill_batch',
            'monthly_report',
            'monthly_narrative',
        ],
        surface: 'desk',
        color: '59, 130, 246',
    },
    {
        code: 'VIGIL',
        fullName: 'Suitability & Mandate Monitoring',
        taskTypes: ['suitability_check'],
        surface: 'comply',
        color: '168, 85, 247',
    },
    {
        code: 'MASON',
        fullName: 'Reconciliation & Break Detection',
        taskTypes: ['reconciliation_classification_batch'],
        surface: 'ops',
        color: '34, 197, 94',
    },
    {
        code: 'AEGIS',
        fullName: 'Compliance Breach & KYC',
        taskTypes: ['breach_draft', 'kyc_renewal', 'consumer_duty_rescore', 'sanctions_screening'],
        surface: 'comply',
        color: '239, 68, 68',
    },
    {
        code: 'SCRIVENER',
        fullName: 'Suitability Letter Drafting',
        taskTypes: ['suitability_letter_draft'],
        surface: 'desk',
        color: '99, 102, 241',
    },
    {
        code: 'HERALD',
        fullName: 'Onboarding Pack Assembly',
        taskTypes: ['onboarding_pack_draft'],
        surface: 'desk',
        color: '20, 184, 166',
    },
    {
        code: 'EMISSARY',
        fullName: 'Client Email Drafting',
        taskTypes: ['email_draft'],
        surface: 'desk',
        color: '244, 114, 182',
    },
    {
        code: 'WARDEN',
        fullName: 'Onboarding Pipeline Watch',
        taskTypes: ['onboarding_pipeline_watch'],
        surface: 'ops',
        color: '251, 146, 60',
    },
    {
        code: 'SENTINEL',
        fullName: 'Vulnerability Detection',
        taskTypes: ['vulnerability_flag'],
        surface: 'comply',
        color: '236, 72, 153',
    },
    {
        code: 'QUORUM',
        fullName: 'Framework Deviation Analysis',
        taskTypes: ['framework_deviation'],
        surface: 'desk',
        color: '139, 92, 246',
    },
    {
        code: 'BEACON',
        fullName: 'Fund Policy Watch',
        taskTypes: ['fund_policy_watch'],
        surface: 'view',
        color: '6, 182, 212',
    },
    {
        code: 'PULSE',
        fullName: 'Data Freshness Check',
        taskTypes: ['data_freshness_check'],
        surface: 'comply',
        color: '132, 204, 22',
    },
    {
        code: 'CHARTER',
        fullName: 'Fund RNS Drafting',
        taskTypes: ['fund_rns_draft'],
        surface: 'view',
        color: '45, 212, 191',
    },
    {
        code: 'HAVEN',
        fullName: 'Vulnerability Adaptation Plans',
        taskTypes: ['vulnerability_adaptation_plan'],
        surface: 'comply',
        color: '248, 113, 113',
    },
    {
        code: 'FORUM',
        fullName: 'Governance Paper Assembly',
        taskTypes: ['governance_paper_draft'],
        surface: 'view',
        color: '129, 140, 248',
    },
    {
        code: 'ARBITER',
        fullName: 'Fair Value Assessment',
        taskTypes: ['fair_value_assessment'],
        surface: 'comply',
        color: '253, 186, 116',
    },
    {
        // WS-A — workflow runs surface on Ops as first-class AgentJobs.
        code: 'WORKFLOW',
        fullName: 'Workflow',
        taskTypes: ['workflow_run'],
        surface: 'ops',
        color: '234, 179, 8',
    },
];

/** Neutral slate accent for agents/items with no registered colour. */
export const NEUTRAL_AGENT_COLOR = '148, 163, 184';

/** Accent colour triplet for an agent code (ATLAS, VIGIL, …). */
export function agentColorForCode(code: string): string {
    return AGENTS.find((a) => a.code === code)?.color ?? NEUTRAL_AGENT_COLOR;
}
